<template>
    <div class="flex flex-col gap-6">
        <p v-if="readOnly" class="text-sm text-theme-muted text-center">
            Ya elegiste tu plan. Puedes cambiarlo cuando quieras desde "Mi
            plan".
        </p>

        <p v-if="error" class="text-sm text-theme-danger text-center">
            {{ error }}
        </p>

        <div v-if="!readOnly" class="flex justify-center gap-6">
            <button
                type="button"
                class="text-sm font-medium text-theme-primary hover:underline"
                @click="showGuideModal = true"
            >
                ¿Cómo elegir?
            </button>
            <button
                type="button"
                class="text-sm font-medium text-theme-primary hover:underline"
                @click="showComparisonModal = true"
            >
                Ver comparativa
            </button>
        </div>

        <p
            v-if="!readOnly && !foreignCountry"
            class="text-sm text-theme-muted text-center"
        >
            Todos los precios están en pesos colombianos (COP).
        </p>
        <div
            v-if="!readOnly && foreignCountry"
            class="bg-theme-info bg-opacity-10 border border-theme-info rounded-lg p-4 text-sm text-theme-base text-center"
        >
            {{ foreignCountry.flag }} Tu WhatsApp es de
            {{ foreignCountry.name }}: los precios de los planes están en
            <strong>pesos colombianos (COP)</strong>, no en la moneda de tu
            país.
        </div>

        <plan-guide-modal v-model="showGuideModal" :plans="modalPlans" />
        <plan-comparison-modal
            v-model="showComparisonModal"
            :plans="modalPlans"
            :monthly-plans="monthlyPlansByCode"
        />

        <div
            v-if="trialRow || freePlan"
            class="grid gap-6 grid-cols-1 items-stretch"
            :class="{ 'md:grid-cols-2': trialRow && freePlan }"
        >
            <div
                v-if="trialRow"
                class="relative flex flex-col gap-4 rounded-2xl p-6 bg-theme-card border-2 border-dashed border-theme-border"
            >
                <span
                    class="absolute -top-3 right-4 bg-theme-primary text-theme-inverse text-xs font-semibold px-3 py-1 rounded-full"
                >
                    Sin tarjeta
                </span>

                <div>
                    <h3 class="text-2xl font-bold text-brand-blue">
                        {{ trialRow.trialDays }} días de {{ trialRow.name }}
                        gratis
                    </h3>
                    <p class="text-sm text-theme-muted mt-1">
                        Usa todo lo de {{ trialRow.name }} sin pagar nada y
                        decide después.
                    </p>
                </div>

                <div>
                    <span class="text-3xl font-bold text-theme-base"> $0 </span>
                    <span class="text-sm text-theme-muted">
                        por {{ trialRow.trialDays }} días</span
                    >
                </div>

                <ul class="flex flex-col gap-2 text-sm text-theme-base">
                    <li
                        v-for="feature in planFeatures(trialRow)"
                        :key="feature"
                        class="flex items-start gap-2"
                    >
                        <span class="text-theme-success">✓</span>
                        {{ feature }}
                    </li>
                </ul>

                <ol
                    class="flex flex-col gap-3 text-sm text-theme-base flex-1 border-t border-theme-border pt-4"
                >
                    <li class="flex items-start gap-2">
                        <span>1️⃣</span>
                        <span>
                            <strong>Hoy:</strong> arrancas con todas las
                            funciones de {{ trialRow.name }}.
                        </span>
                    </li>
                    <li class="flex items-start gap-2">
                        <span>2️⃣</span>
                        <span>
                            <strong>Durante la prueba:</strong> si te sirve,
                            sigues con {{ trialRow.name }} pagando mensual,
                            trimestral o anual.
                        </span>
                    </li>
                    <li class="flex items-start gap-2">
                        <span>3️⃣</span>
                        <span>
                            <strong>Día {{ trialRow.trialDays }}:</strong> si no
                            pagas, pasas solo al plan
                            {{ freePlan ? freePlan.name : "gratis" }}. No te
                            cobramos nada.
                        </span>
                    </li>
                </ol>

                <div v-if="!readOnly" class="flex flex-col gap-2">
                    <app-button
                        border
                        color="brand-blue"
                        :loading="isSubmitting(trialRow, 'trial')"
                        :disabled="isBusy"
                        @click="choosePlan(trialRow, 'trial')"
                    >
                        Empezar mi prueba gratis
                    </app-button>
                </div>
            </div>

            <div
                v-if="freePlan"
                class="relative flex flex-col gap-4 rounded-2xl p-6 bg-theme-card border-2 border-dashed border-theme-border w-full"
                :class="{ 'md:max-w-md md:mx-auto': !trialRow }"
            >
                <div>
                    <h3 class="text-2xl font-bold text-theme-base">
                        {{ freePlan.name }}
                    </h3>
                    <p
                        v-if="freePlan.description"
                        class="text-sm text-theme-muted mt-1"
                    >
                        {{ freePlan.description }}
                    </p>
                </div>

                <div>
                    <span class="text-3xl font-bold text-theme-base">
                        Gratis
                    </span>
                </div>

                <ul class="flex flex-col gap-2 text-sm text-theme-base flex-1">
                    <li
                        v-for="feature in planFeatures(freePlan)"
                        :key="feature"
                        class="flex items-start gap-2"
                    >
                        <span class="text-theme-success">✓</span>
                        {{ feature }}
                    </li>
                    <li
                        v-for="limitation in freePlanLimitations"
                        :key="limitation"
                        class="flex items-start gap-2 text-theme-muted"
                    >
                        {{ limitation }}
                    </li>
                </ul>

                <div v-if="!readOnly" class="flex flex-col gap-2">
                    <app-button
                        border
                        color="brand-blue"
                        :loading="isSubmitting(freePlan, 'paid')"
                        :disabled="isBusy"
                        @click="choosePlan(freePlan, 'paid')"
                    >
                        Crear mi perfil gratis
                    </app-button>
                </div>
            </div>
        </div>
        <h2
            v-if="paidGroups.length && (trialRow || freePlan)"
            class="text-xl font-bold text-theme-base text-center mt-4"
        >
            O paga el plan que necesitas
        </h2>

        <div v-if="!readOnly && periods.length > 1" class="flex justify-center">
            <plan-period-selector v-model="selectedPeriod" :periods="periods" />
        </div>

        <div
            v-if="paidGroups.length"
            class="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-stretch"
        >
            <div
                v-for="group in paidGroups"
                :key="group.code"
                class="relative flex flex-col gap-4 rounded-2xl p-6 bg-theme-card"
                :class="cardBorderClass(group)"
            >
                <span
                    v-if="isRecommended(group)"
                    class="absolute -top-3 right-4 bg-theme-primary text-theme-inverse text-xs font-semibold px-3 py-1 rounded-full"
                >
                    Recomendado
                </span>
                <span
                    v-else-if="group.code === intentCode"
                    class="absolute -top-3 right-4 bg-theme-primary text-theme-inverse text-xs font-semibold px-3 py-1 rounded-full"
                >
                    Elegido en la página
                </span>

                <div>
                    <h3
                        class="text-2xl font-bold"
                        :class="cardTitleClass(group)"
                    >
                        {{ group.name }}
                    </h3>
                    <p
                        v-if="group.description"
                        class="text-sm text-theme-muted mt-1"
                    >
                        {{ group.description }}
                    </p>
                </div>

                <div>
                    <p class="whitespace-nowrap">
                        <span class="text-3xl font-bold text-theme-base">
                            ${{ rowOf(group).price.toLocaleString() }}
                        </span>
                        <span class="text-sm text-theme-muted">
                            {{ periodSuffix(rowOf(group)) }}</span
                        >
                    </p>
                    <p
                        v-if="rowOf(group).period !== 'monthly'"
                        class="text-sm text-theme-muted mt-1"
                    >
                        Equivale a ${{
                            monthlyEquivalent(rowOf(group)).toLocaleString()
                        }}
                        al mes
                    </p>
                    <p
                        v-if="savings(group) > 0"
                        class="text-sm font-bold text-theme-success"
                    >
                        Ahorras ${{ savingsAmount(group).toLocaleString() }} ({{
                            savings(group)
                        }}
                        %)
                    </p>
                </div>

                <ul class="flex flex-col gap-2 text-sm text-theme-base flex-1">
                    <li
                        v-for="feature in planFeatures(rowOf(group))"
                        :key="feature"
                        class="flex items-start gap-2"
                    >
                        <span class="text-theme-success">✓</span>
                        {{ feature }}
                    </li>
                </ul>

                <div v-if="!readOnly" class="flex flex-col gap-2">
                    <app-button
                        color="brand-blue"
                        :loading="isSubmitting(rowOf(group), 'paid')"
                        :disabled="isBusy"
                        @click="choosePlan(rowOf(group), 'paid')"
                    >
                        Pagar ahora
                    </app-button>
                </div>
            </div>
        </div>

        <legal-payment-notice
            v-if="!readOnly && paidGroups.length"
            class="text-center"
        />
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import { mapActions } from "vuex";

import { LANDING_PLAN_INTENT_STORAGE_KEY } from "@/common/constants";
import {
    FREE_PLAN_LIMITATIONS,
    TIER_ACCENT_CLASSES,
    buildPlanFeatures
} from "@/common/fields/plan-content";
import {
    buildPaymentOpeningRoute,
    savePaymentLink
} from "@/common/lib/payment-link";
import {
    PLAN_PERIOD_SUFFIXES,
    availablePeriods,
    groupPlansByCode,
    isPlanPeriod,
    monthlyEquivalent,
    rowForPeriod,
    savingsAmount,
    savingsPercent
} from "@/common/lib/plan-periods";
import {
    DEFAULT_WHATSAPP_COUNTRY,
    getWhatsappCountry,
    splitWhatsappNumber
} from "@/common/lib/whatsapp";

import LegalPaymentNotice from "@/components/legal/LegalPaymentNotice.vue";
import PlanComparisonModal from "@/components/onboarding/PlanComparisonModal.vue";
import PlanGuideModal from "@/components/onboarding/PlanGuideModal.vue";
import PlanPeriodSelector from "@/components/utils/PlanPeriodSelector.vue";

const FREE_PLAN_CODE = "free";

export default {
    name: "OnboardingPlanPage",

    components: {
        LegalPaymentNotice,
        PlanComparisonModal,
        PlanGuideModal,
        PlanPeriodSelector
    },

    mixins: [onboardingCompletedGuard, onboardingStepNavigation],

    data() {
        return {
            stepKey: "plan",
            plans: [],
            selectedPeriod: "monthly",
            // `code` que el visitante eligió en la landing (si llegó de ahí).
            intentCode: null,
            submitting: null,
            error: null,
            showGuideModal: false,
            showComparisonModal: false
        };
    },

    computed: {
        // El único país que se pide en el onboarding es el del WhatsApp.
        foreignCountry() {
            const { country } = splitWhatsappNumber(
                this.selections?.whatsappNumber,
                DEFAULT_WHATSAPP_COUNTRY
            );
            return country === DEFAULT_WHATSAPP_COUNTRY
                ? null
                : getWhatsappCountry(country);
        },
        isBusy() {
            return Boolean(this.submitting);
        },
        groups() {
            return groupPlansByCode(this.plans);
        },
        // Un grupo por plan pago (con una fila por periodo); free se muestra
        // aparte, como salida secundaria, no como un 4º plan compitiendo con
        // los pagos.
        paidGroups() {
            return this.groups.filter((group) => group.code !== FREE_PLAN_CODE);
        },
        periods() {
            return availablePeriods(this.paidGroups);
        },
        // Comparativa y guía muestran las filas del periodo elegido + Free.
        modalPlans() {
            return [
                ...this.paidGroups.map((group) => this.rowOf(group)),
                ...(this.freePlan ? [this.freePlan] : [])
            ];
        },
        monthlyPlansByCode() {
            return this.paidGroups.reduce((acc, group) => {
                if (group.rows.monthly) {
                    acc[group.code] = group.rows.monthly;
                }
                return acc;
            }, {});
        },
        // La prueba se ofrece en su propia tarjeta, junto a Free, en vez de
        // un botón dentro de la tarjeta del plan: así quien busca empezar
        // sin pagar ve las dos salidas lado a lado. Es una sola fila (la
        // mensual del plan con trial) y no depende del periodo elegido.
        trialRow() {
            return (
                this.paidGroups
                    .map((group) => this.trialRowOf(group))
                    .find(Boolean) || null
            );
        },
        freePlan() {
            return this.plans.find((plan) => this.isFree(plan)) || null;
        },
        freePlanLimitations() {
            return FREE_PLAN_LIMITATIONS;
        },
        // La elección se hace con los botones de cada tarjeta: "Siguiente"
        // del layout solo avanza cuando el step ya está elegido (bloqueado).
        localStepperUi() {
            return {
                nextDisabled: !this.readOnly,
                loading: this.isBusy,
                nextLabel: "Continuar",
                nextVariant: "primary"
            };
        }
    },

    async mounted() {
        await this.ensureOnboardingStatus();
        if (this.redirectIfOnboardingCompleted()) return;
        this.$loading.show();
        try {
            const { items } = await this.getOnboardingPlans();
            this.plans = items;
            this.applyLandingIntent();
            this.$loading.hide();
        } catch (err) {
            this.$loading.hide();
            this.error = err.message || "No pudimos cargar los planes";
        }
    },

    methods: {
        ...mapActions("onboarding", ["getOnboardingPlans", "submitPlan"]),
        ...mapActions("auth", ["applyAllPermissions", "getProfile"]),

        isFree(plan) {
            return plan.code === FREE_PLAN_CODE;
        },

        // Fila del grupo para el periodo elegido (la mensual si ese plan no
        // tiene ese periodo).
        rowOf(group) {
            return rowForPeriod(group, this.selectedPeriod);
        },

        periodSuffix(row) {
            return PLAN_PERIOD_SUFFIXES[row.period];
        },

        monthlyEquivalent(row) {
            return monthlyEquivalent(row);
        },

        savings(group) {
            return savingsPercent(this.rowOf(group), group.rows.monthly);
        },

        savingsAmount(group) {
            return savingsAmount(this.rowOf(group), group.rows.monthly);
        },

        // Recomendado: algún periodo del plan tiene trial disponible. Se
        // evalúa sobre el grupo para que la etiqueta no parpadee al cambiar
        // de periodo.
        isRecommended(group) {
            return Boolean(this.trialRowOf(group));
        },

        // La prueba no depende del periodo: el backend solo la ofrece en una
        // fila (la mensual), pero el botón se muestra en todos los periodos
        // y siempre envía esa fila; el periodo se elige al terminar la prueba.
        trialRowOf(group) {
            return (
                Object.values(group.rows).find((row) => row.trialAvailable) ||
                null
            );
        },

        // Plan y periodo que mandó la landing (`?plan=<code>&period=`): se
        // lee una sola vez y se borra, valga o no. Un plan o periodo que no
        // existe se ignora sin error.
        applyLandingIntent() {
            let intent = null;
            try {
                intent = JSON.parse(
                    sessionStorage.getItem(LANDING_PLAN_INTENT_STORAGE_KEY)
                );
                sessionStorage.removeItem(LANDING_PLAN_INTENT_STORAGE_KEY);
            } catch (err) {
                return;
            }
            if (!intent || typeof intent.plan !== "string") return;
            const group = this.paidGroups.find(
                (item) => item.code === intent.plan
            );
            if (!group) return;
            this.intentCode = group.code;
            if (isPlanPeriod(intent.period) && group.rows[intent.period]) {
                this.selectedPeriod = intent.period;
            }
        },

        // El plan recomendado se destaca con borde grueso y sombra ademas del
        // color de su tier; el elegido en la landing, con el mismo peso; los
        // demas planes pagos solo llevan el color, sin ese peso extra.
        cardBorderClass(group) {
            const accent = TIER_ACCENT_CLASSES[group.code];
            const colorClass = accent ? accent.border : "border-theme-border";
            return this.isRecommended(group) || group.code === this.intentCode
                ? `border-2 ${colorClass} shadow-lg`
                : `border ${colorClass}`;
        },

        cardTitleClass(group) {
            const accent = TIER_ACCENT_CLASSES[group.code];
            return accent ? accent.text : "text-theme-base";
        },

        isSubmitting(plan, mode) {
            return this.submitting === `${plan.slug}:${mode}`;
        },

        planFeatures(plan) {
            return buildPlanFeatures(plan);
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
            }
        },

        async choosePlan(plan, mode) {
            if (this.isBusy) return;
            this.submitting = `${plan.slug}:${mode}`;
            this.error = null;
            try {
                const data = await this.submitPlan({
                    planSlug: plan.slug,
                    mode
                });
                this.$analytics.trackEvent("onboarding_plan_selected", {
                    plan_code: plan.code,
                    plan_slug: plan.slug,
                    mode: this.isFree(plan) ? "free" : mode,
                    period: plan.period
                });
                if (data.paymentUrl) {
                    savePaymentLink(data.invoiceId, data.paymentUrl);
                    this.$router.push(
                        buildPaymentOpeningRoute("onboarding", data.invoiceId)
                    );
                    return;
                }
                if (data.permissions) {
                    this.applyAllPermissions({
                        permissions: data.permissions
                    });
                }
                // La sesión se cargó antes de tener plan: sin refrescarla,
                // `subscription` sigue en null y /app no muestra la tarjeta
                // del plan. Si falla, el plan ya quedó guardado y se sigue.
                await this.getProfile().catch(() => null);
                this.submitting = null;
                this.navigateByFlow(data.onboardingFlow);
            } catch (err) {
                this.submitting = null;
                this.error = err.message || "No pudimos guardar tu plan";
            }
        },

        navigateByFlow(flow) {
            if (!flow) return;
            const step =
                flow.status === "completed" ? flow.nextStep : flow.step;
            const route = step ? this.routeForStep(step) : null;
            this.$router.push(route || this.completionRoute);
        }
    }
};
</script>
